import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_ADMIN_PERFORMANCE = "/api/employee-performance-summary.php";
const API_GIFT_AWARD_LIST = "/api/gift-award-list.php";
const API_GIFT_AWARD_SAVE = "/api/gift-award-save.php";
const API_GIFT_AWARD_BULK_SAVE = "/api/gift-award-bulk-save.php";
const API_GIFT_AWARD_PUBLISH = "/api/gift-award-publish.php";
const API_GIFT_AWARD_UNPUBLISH = "/api/gift-award-unpublish.php";
const API_GIFT_AWARD_DELETE = "/api/gift-award-delete.php";
const API_GIFT_AWARD_IMPORT_PREVIEW = "/api/gift-award-import-preview.php";
const API_GIFT_AWARD_DELIVERY_UPDATE = "/api/gift-award-delivery-update.php";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

function formatDuration(seconds) {
  const total = Math.max(0, safeNumber(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours} sa ${minutes} dk`;
}

function toInputDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthRange(periodMonth, offset = 0) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  const now = new Date();
  const year = match ? Number(match[1]) : now.getFullYear();
  const monthIndex = (match ? Number(match[2]) - 1 : now.getMonth()) + offset;
  return {
    start: toInputDate(new Date(year, monthIndex, 1)),
    end: toInputDate(new Date(year, monthIndex + 1, 0)),
  };
}

function periodLabel(periodMonth) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return periodMonth || "-";
  return `${MONTHS_TR[Number(match[2]) - 1] || match[2]} ${match[1]}`;
}

function getHiddenId(item) {
  return String(item?.hiddenId || item?.hidden_id || item?.hiddenID || item?.hiddenid || "").trim();
}

function deduplicatePublishersByHiddenId(publishers = []) {
  if (!Array.isArray(publishers) || publishers.length <= 1) {
    return publishers || [];
  }

  const seenHiddenIds = new Map();
  const noHiddenIdList = [];

  publishers.forEach((pub, index) => {
    const rawHiddenId = getHiddenId(pub);

    if (!rawHiddenId || rawHiddenId === "-" || rawHiddenId === "0" || rawHiddenId.toLowerCase() === "null") {
      noHiddenIdList.push(pub);
      return;
    }

    const key = rawHiddenId.toLowerCase();
    const existing = seenHiddenIds.get(key);

    if (!existing) {
      seenHiddenIds.set(key, { ...pub, _originalIndex: index });
    } else {
      const existingId = Number(existing.id || existing.publisherId || existing.userId) || 0;
      const currentId = Number(pub.id || pub.publisherId || pub.userId) || 0;

      const existingCreatedAt = existing.createdAt || existing.raw?.createdAt || existing.raw?.created_at || "";
      const currentCreatedAt = pub.createdAt || pub.raw?.createdAt || pub.raw?.created_at || "";

      let isCurrentNewer = false;

      if (currentId > 0 && existingId > 0 && currentId !== existingId) {
        isCurrentNewer = currentId > existingId;
      } else if (existingCreatedAt && currentCreatedAt && existingCreatedAt !== currentCreatedAt) {
        const timeExisting = new Date(String(existingCreatedAt).replace(" ", "T")).getTime() || 0;
        const timeCurrent = new Date(String(currentCreatedAt).replace(" ", "T")).getTime() || 0;
        isCurrentNewer = timeCurrent >= timeExisting;
      } else {
        isCurrentNewer = true;
      }

      if (isCurrentNewer) {
        seenHiddenIds.set(key, { ...pub, _originalIndex: index });
      }
    }
  });

  const deduplicated = Array.from(seenHiddenIds.values()).map((item) => {
    const { _originalIndex, ...rest } = item;
    return rest;
  });

  return [...deduplicated, ...noHiddenIdList];
}

function normalizeUsername(value) {
  return String(value || "").trim().replace(/^@/, "").replace(/\s+/g, "").toLocaleLowerCase("tr-TR");
}

function getTierNo(diamonds) {
  const value = safeNumber(diamonds);
  if (value >= 3000000) return 10;
  if (value >= 1600000) return 9;
  if (value >= 1200000) return 8;
  if (value >= 800000) return 7;
  if (value >= 500000) return 6;
  if (value >= 300000) return 5;
  if (value >= 150000) return 4;
  if (value >= 80000) return 3;
  if (value >= 40000) return 2;
  return 1;
}

function tierChange(previousTier, currentTier) {
  const diff = safeNumber(currentTier) - safeNumber(previousTier);
  if (diff > 0) return `${diff} Kademe Arttı`;
  if (diff < 0) return `${Math.abs(diff)} Kademe Düştü`;
  return "Kademe Korundu";
}

function tierChangeGroup(text) {
  const value = String(text || "").toLocaleLowerCase("tr-TR");
  if (value.includes("arttı")) return "up";
  if (value.includes("düştü")) return "down";
  return "same";
}

function tierChangeClass(text) {
  const group = tierChangeGroup(text);
  if (group === "up") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (group === "down") return "border-red-500/30 bg-red-500/10 text-red-200";
  return "border-white/15 bg-white/[0.04] text-white/65";
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, options);
  const raw = await response.text();
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  const trimmed = String(raw || "").trim();

  let data;
  try {
    data = trimmed ? JSON.parse(trimmed) : {};
  } catch {
    const looksLikeHtml =
      contentType.includes("text/html") ||
      /^<!doctype\s+html/i.test(trimmed) ||
      /^<html/i.test(trimmed);

    if (looksLikeHtml) {
      throw new Error(
        `Hediye API servisi JSON yerine web sayfası döndürdü. ${url} dosyasının httpdocs/api/ içinde bulunduğunu kontrol edin.`
      );
    }

    throw new Error(
      `Hediye API servisi geçerli JSON döndürmedi. ${url} dosyasını kontrol edin.`
    );
  }

  if (!response.ok || data?.success === false) {
    throw new Error(
      data?.message ||
        data?.error ||
        `İşlem başarısız. HTTP ${response.status} (${url})`
    );
  }

  return data;
}

async function fetchPerformance(hiddenIds, range) {
  const ids = [...new Set((hiddenIds || []).filter(Boolean))];
  if (!ids.length) return { publishers: [], publisherDaily: [] };
  const aggregate = { publishers: [], publisherDaily: [] };
  const chunks = [];
  for (let i = 0; i < ids.length; i += 40) chunks.push(ids.slice(i, i + 40));

  for (const chunk of chunks) {
    const url = `${API_ADMIN_PERFORMANCE}?hiddenIds=${encodeURIComponent(chunk.join(","))}&startDate=${range.start}&endDate=${range.end}`;
    // eslint-disable-next-line no-await-in-loop
    const result = await requestJson(url, { cache: "no-store" });
    (result.publishers || []).forEach((row) => aggregate.publishers.push(row));
    (result.publisherDaily || result.dailyByPublisher || []).forEach((row) => aggregate.publisherDaily.push(row));

    // Eski API çoklu Hidden ID için yayıncı-gün detayı vermiyorsa Aktif Gün yanlış hesaplanmasın.
    if (chunk.length > 1 && !(result.publisherDaily || result.dailyByPublisher)) {
      for (const hiddenId of chunk) {
        const singleUrl = `${API_ADMIN_PERFORMANCE}?hiddenIds=${encodeURIComponent(hiddenId)}&startDate=${range.start}&endDate=${range.end}`;
        try {
          // eslint-disable-next-line no-await-in-loop
          const single = await requestJson(singleUrl, { cache: "no-store" });
          const daily = single.publisherDaily || (single.daily || []).map((item) => ({ ...item, hiddenId }));
          daily.forEach((row) => aggregate.publisherDaily.push(row));
        } catch {
          // Bir kullanıcıdaki hata diğerlerini engellemez.
        }
      }
    }
  }
  return aggregate;
}

function perfRow(performance, hiddenId) {
  return (performance?.publishers || []).find((item) => String(getHiddenId(item)) === String(hiddenId)) || {};
}

function activeDays(performance, hiddenId) {
  const dates = (performance?.publisherDaily || [])
    .filter((item) => String(getHiddenId(item)) === String(hiddenId))
    .filter((item) => safeNumber(item.liveCount || item.live_count) > 0 || safeNumber(item.liveSeconds || item.live_seconds) > 0)
    .map((item) => String(item.report_date || item.reportDate || item.date || "").slice(0, 10))
    .filter(Boolean);
  return new Set(dates).size;
}

function buildActivityRows(publishers, managers, current, previous) {
  const managerMap = {};
  (managers || []).forEach((manager) => {
    if (manager?.username) managerMap[String(manager.username)] = manager.fullName || manager.username;
  });

  const uniquePublishers = deduplicatePublishersByHiddenId(publishers || []);

  return uniquePublishers.map((publisher) => {
    const hiddenId = getHiddenId(publisher);
    const currentPerf = perfRow(current, hiddenId);
    const previousPerf = perfRow(previous, hiddenId);
    const diamonds = safeNumber(currentPerf.totalDiamonds || currentPerf.total_diamonds || currentPerf.diamonds);
    const oldDiamonds = safeNumber(previousPerf.totalDiamonds || previousPerf.total_diamonds || previousPerf.diamonds);
    const liveCount = safeNumber(currentPerf.totalLiveCount || currentPerf.total_live_count || currentPerf.liveCount);
    const liveSeconds = safeNumber(currentPerf.totalLiveSeconds || currentPerf.total_live_seconds || currentPerf.liveSeconds);
    const previousTier = getTierNo(oldDiamonds);
    const currentTier = getTierNo(diamonds);
    const managerUsername = publisher.assignedEmployeeUsername || publisher.employeeUsername || publisher.managerUsername || publisher.representative || "";

    return {
      publisherId: publisher.id || publisher.userId || null,
      publisherUsername: publisher.username || publisher.userName || "-",
      publisherName: publisher.fullName || publisher.name || publisher.username || "-",
      hiddenId,
      managerUsername,
      managerName: publisher.managerName || managerMap[managerUsername] || managerUsername || "-",
      previousTier,
      currentTier,
      tierChange: tierChange(previousTier, currentTier),
      activeDays: activeDays(current, hiddenId),
      liveCount,
      liveSeconds,
      liveHours: Math.round((liveSeconds / 3600) * 10) / 10,
      diamonds,
    };
  });
}

function useMonthlyActivity(publisherRows, managerRows, periodMonth) {
  const [state, setState] = useState({ loading: true, error: "", rows: [] });
  useEffect(() => {
    let ignore = false;
    async function load() {
      const uniquePublishers = deduplicatePublishersByHiddenId(publisherRows || []);
      const hiddenIds = uniquePublishers.map(getHiddenId).filter((id) => id && id !== "-");
      if (!hiddenIds.length) {
        setState({ loading: false, error: "Geçerli Hidden ID bulunamadı.", rows: [] });
        return;
      }
      try {
        setState((prev) => ({ ...prev, loading: true, error: "" }));
        const [current, previous] = await Promise.all([
          fetchPerformance(hiddenIds, monthRange(periodMonth, 0)),
          fetchPerformance(hiddenIds, monthRange(periodMonth, -1)),
        ]);
        if (!ignore) setState({ loading: false, error: "", rows: buildActivityRows(uniquePublishers, managerRows, current, previous) });
      } catch (error) {
        if (!ignore) setState({ loading: false, error: error.message || "Performans verisi alınamadı.", rows: [] });
      }
    }
    load();
    return () => { ignore = true; };
  }, [publisherRows, managerRows, periodMonth]);
  return state;
}

function Card({ children, className = "" }) {
  return <div className={`rounded-[1.45rem] border border-white/[0.08] bg-white/[0.03] shadow-[0_18px_60px_rgba(0,0,0,.32)] ${className}`}>{children}</div>;
}

function Button({ children, onClick, variant = "dark", disabled = false, type = "button", className = "" }) {
  const tone = variant === "gold"
    ? "border-yellow-500/35 bg-yellow-500 text-black hover:bg-yellow-300"
    : variant === "red"
      ? "border-red-500/25 bg-red-500/10 text-red-200 hover:bg-red-500/20"
      : variant === "green"
        ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500 hover:text-black"
        : "border-white/10 bg-white/[0.04] text-white/70 hover:border-yellow-500/30 hover:text-yellow-200";
  return <button type={type} onClick={onClick} disabled={disabled} className={`rounded-xl border px-4 py-2.5 text-xs font-black transition disabled:cursor-not-allowed disabled:opacity-45 ${tone} ${className}`}>{children}</button>;
}

function SectionHeader({ title, desc, action }) {
  return (
    <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.34em] text-yellow-300/80">Hediye Prim Yönetimi</div>
        <h2 className="mt-2 text-2xl font-black text-white lg:text-3xl">{title}</h2>
        <p className="mt-2 max-w-4xl text-[13px] leading-6 text-white/48">{desc}</p>
      </div>
      {action}
    </div>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-[11px] font-bold text-white/45">{label}</span>
      <select value={value} onChange={onChange} className="w-full rounded-xl border border-white/10 bg-[#0b0b0b] px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-500/35 [color-scheme:dark]">{children}</select>
    </label>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-[11px] font-bold text-white/45">{label}</span>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full rounded-xl border border-white/10 bg-[#0b0b0b] px-3 py-2.5 text-xs text-white outline-none placeholder:text-white/25 focus:border-yellow-500/35" />
    </label>
  );
}

function MiniMetric({ label, value }) {
  return <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3"><div className="text-[9px] font-black uppercase tracking-[0.16em] text-white/35">{label}</div><div className="mt-1 break-words text-sm font-black text-white">{value}</div></div>;
}

function matchesHours(hours, filter) {
  if (!filter) return true;
  if (filter === "0") return hours === 0;
  if (filter === "100+") return hours >= 100;
  const [min, max] = filter.split("-").map(Number);
  return hours > min && hours <= max;
}

function matchesDiamonds(diamonds, filter) {
  if (!filter) return true;
  if (filter === "0") return diamonds === 0;
  const ranges = { "1-10k": [1, 10000], "10-50k": [10001, 50000], "50-100k": [50001, 100000], "100-200k": [100001, 200000], "200-500k": [200001, 500000], "500k+": [500001, Infinity] };
  const range = ranges[filter];
  return !range || (diamonds >= range[0] && diamonds <= range[1]);
}

function AwardModal({ row, periodMonth, currentAdmin, onClose }) {
  const [giftDetail, setGiftDetail] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  if (!row) return null;

  async function save(publishStatus) {
    if (!giftDetail.trim()) return setError("Hediye Detayı zorunludur.");
    try {
      setSaving(true);
      setError("");
      await requestJson(API_GIFT_AWARD_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ periodMonth, ...row, giftDetail: giftDetail.trim(), adminNote: adminNote.trim(), publishStatus, deliveryStatus: "Bekliyor", createdBy: currentAdmin?.username || "admin", publishedBy: publishStatus === "Yayınlandı" ? currentAdmin?.username || "admin" : "" }),
      });
      onClose?.();
    } catch (err) {
      setError(err.message || "Hediye kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/80 p-4">
      <div className="w-full max-w-2xl rounded-[1.8rem] border border-yellow-500/25 bg-[#090909] p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4"><div><div className="text-[10px] font-black uppercase tracking-[0.28em] text-yellow-300">Hediye / Prim Belirle</div><h3 className="mt-2 text-2xl font-black text-white">{row.publisherUsername}</h3><div className="mt-1 text-xs text-white/40">{periodLabel(periodMonth)}</div></div><Button onClick={onClose}>Kapat</Button></div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <MiniMetric label="Eski Kademe" value={`Kademe ${row.previousTier}`} /><MiniMetric label="Güncel Kademe" value={`Kademe ${row.currentTier}`} /><MiniMetric label="Kademe Değişimi" value={row.tierChange} /><MiniMetric label="Aktif Gün" value={row.activeDays} /><MiniMetric label="Yayın Adedi" value={formatNumber(row.liveCount)} /><MiniMetric label="Yayın Süresi" value={formatDuration(row.liveSeconds)} /><MiniMetric label="Elmas" value={formatNumber(row.diamonds)} /><MiniMetric label="Hidden ID" value={row.hiddenId || "-"} /><MiniMetric label="Yönetici" value={row.managerName || "-"} />
        </div>
        <label className="mt-5 block"><span className="mb-2 block text-xs font-black text-white/65">Hediye Detayı</span><input value={giftDetail} onChange={(e) => setGiftDetail(e.target.value)} placeholder="32.000 Elmas, Balina Ödülü, Telefon Ödülü..." className="w-full rounded-2xl border border-yellow-500/25 bg-black/40 px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-yellow-500/55" /></label>
        <label className="mt-4 block"><span className="mb-2 block text-xs font-black text-white/65">Yönetici Notu</span><textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none focus:border-yellow-500/35" /></label>
        {error && <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-200">{error}</div>}
        <div className="mt-5 flex flex-wrap justify-end gap-2"><Button onClick={() => save("Taslak")} disabled={saving}>Taslak Kaydet</Button><Button variant="gold" onClick={() => save("Yayınlandı")} disabled={saving}>Kaydet ve Yayınla</Button></div>
      </div>
    </div>
  );
}

export function GiftActiveDetailsContent({ publisherRows = [], managerRows = [], currentAdmin = {} }) {
  const [periodMonth, setPeriodMonth] = useState(defaultMonth());
  const { loading, error, rows } = useMonthlyActivity(publisherRows, managerRows, periodMonth);
  const [filters, setFilters] = useState({ manager: "", publisher: "", previousTier: "", currentTier: "", tierChange: "", activeDays: "", hours: "", diamonds: "" });
  const [giftDrafts, setGiftDrafts] = useState({});
  const [publishingKey, setPublishingKey] = useState("");
  const [rowMessages, setRowMessages] = useState({});

  const managers = useMemo(() => [...new Set(rows.map((row) => row.managerName).filter((v) => v && v !== "-"))].sort(), [rows]);
  const filtered = useMemo(() => rows.filter((row) => {
    const query = normalizeUsername(filters.publisher);
    if (filters.manager && row.managerName !== filters.manager) return false;
    if (query && !normalizeUsername(`${row.publisherUsername}${row.publisherName}`).includes(query)) return false;
    if (filters.previousTier && String(row.previousTier) !== filters.previousTier) return false;
    if (filters.currentTier && String(row.currentTier) !== filters.currentTier) return false;
    if (filters.tierChange && tierChangeGroup(row.tierChange) !== filters.tierChange) return false;
    if (filters.activeDays) {
      if (filters.activeDays === "30+" ? row.activeDays < 30 : String(row.activeDays) !== filters.activeDays) return false;
    }
    if (!matchesHours(row.liveHours, filters.hours)) return false;
    if (!matchesDiamonds(row.diamonds, filters.diamonds)) return false;
    return true;
  }), [rows, filters]);

  const reset = () => setFilters({ manager: "", publisher: "", previousTier: "", currentTier: "", tierChange: "", activeDays: "", hours: "", diamonds: "" });
  const rowKey = (row) => `${row.hiddenId || "noid"}:${normalizeUsername(row.publisherUsername)}`;

  async function publishInlineGift(row) {
    const key = rowKey(row);
    const giftDetail = String(giftDrafts[key] || "").trim();

    if (!giftDetail) {
      setRowMessages((prev) => ({ ...prev, [key]: { type: "error", text: "Hediye bilgisini yazın." } }));
      return;
    }

    try {
      setPublishingKey(key);
      setRowMessages((prev) => ({ ...prev, [key]: null }));
      await requestJson(API_GIFT_AWARD_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          periodMonth,
          ...row,
          giftDetail,
          adminNote: "",
          publishStatus: "Yayınlandı",
          deliveryStatus: "Bekliyor",
          createdBy: currentAdmin?.username || "admin",
          publishedBy: currentAdmin?.username || "admin",
        }),
      });

      setGiftDrafts((prev) => ({ ...prev, [key]: "" }));
      setRowMessages((prev) => ({ ...prev, [key]: { type: "success", text: "Yayınlandı" } }));
    } catch (err) {
      setRowMessages((prev) => ({ ...prev, [key]: { type: "error", text: err.message || "Yayınlanamadı." } }));
    } finally {
      setPublishingKey("");
    }
  }

  return (
    <div>
      <SectionHeader
        title="Yayıncı Aktiflik Detayları"
        desc="Seçilen ayda yayıncıların aktif gün, yayın adedi, yayın süresi, elmas ve kademe değişimi gösterilir. Aynı gün 3 yayın açılsa bile Aktif Gün yalnızca 1 sayılır. Hediye / Prim alanına doğrudan yazıp Yayınla butonuyla yayıncı ekranına gönderebilirsiniz."
        action={<div className="rounded-full border border-yellow-500/25 bg-yellow-500/10 px-4 py-2 text-xs font-black text-yellow-200">{filtered.length} yayıncı</div>}
      />

      <Card className="p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <Input label="Ay" type="month" value={periodMonth} onChange={(e) => setPeriodMonth(e.target.value)} />
          <Select label="Yönetici" value={filters.manager} onChange={(e) => setFilters((p) => ({ ...p, manager: e.target.value }))}><option value="">Tümü</option>{managers.map((m) => <option key={m}>{m}</option>)}</Select>
          <Input label="Yayıncı" value={filters.publisher} onChange={(e) => setFilters((p) => ({ ...p, publisher: e.target.value }))} placeholder="Kullanıcı adı ara" />
          <Select label="Eski Kademe" value={filters.previousTier} onChange={(e) => setFilters((p) => ({ ...p, previousTier: e.target.value }))}><option value="">Tümü</option>{Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>Kademe {n}</option>)}</Select>
          <Select label="Güncel Kademe" value={filters.currentTier} onChange={(e) => setFilters((p) => ({ ...p, currentTier: e.target.value }))}><option value="">Tümü</option>{Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>Kademe {n}</option>)}</Select>
          <Select label="Kademe Değişimi" value={filters.tierChange} onChange={(e) => setFilters((p) => ({ ...p, tierChange: e.target.value }))}><option value="">Tümü</option><option value="up">Kademe Arttı</option><option value="same">Kademe Korundu</option><option value="down">Kademe Düştü</option></Select>
          <Select label="Aktif Gün" value={filters.activeDays} onChange={(e) => setFilters((p) => ({ ...p, activeDays: e.target.value }))}><option value="">Tümü</option>{Array.from({ length: 30 }, (_, i) => i).map((n) => <option key={n} value={n}>{n} Gün</option>)}<option value="30+">30+ Gün</option></Select>
          <Select label="Yayın Süresi" value={filters.hours} onChange={(e) => setFilters((p) => ({ ...p, hours: e.target.value }))}><option value="">Tümü</option><option value="0">0 Saat</option><option value="0-10">1-10 Saat</option>{[10,20,30,40,50,60,70,80,90].map((n) => <option key={n} value={`${n}-${n+10}`}>{n}-{n+10} Saat</option>)}<option value="100+">100+ Saat</option></Select>
          <Select label="Elmas Kazanımı" value={filters.diamonds} onChange={(e) => setFilters((p) => ({ ...p, diamonds: e.target.value }))}><option value="">Tümü</option><option value="0">0</option><option value="1-10k">1-10K</option><option value="10-50k">10-50K</option><option value="50-100k">50-100K</option><option value="100-200k">100-200K</option><option value="200-500k">200-500K</option><option value="500k+">500K ve üzeri</option></Select>
          <div className="flex items-end"><Button onClick={reset} className="w-full">Filtreleri Temizle</Button></div>
        </div>
      </Card>

      {loading && <Card className="mt-4 p-5 text-sm text-white/50">Performans verileri yükleniyor...</Card>}
      {error && <Card className="mt-4 border-red-500/20 bg-red-500/10 p-5 text-sm font-bold text-red-200">{error}</Card>}

      {!loading && !error && (
        <Card className="mt-5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1650px] w-full border-collapse text-left text-xs">
              <thead className="bg-white/[0.04] text-[10px] font-black uppercase tracking-[0.13em] text-white/40">
                <tr>
                  {[
                    "Kullanıcı Adı", "Hidden ID", "Yönetici", "Eski Kademe", "Güncel Kademe",
                    "Kademe Değişimi", "Aktif Gün", "Yayın Adedi", "Yayın Süresi", "Elmas",
                    "Hediye / Prim", "İşlem"
                  ].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const key = rowKey(row);
                  const message = rowMessages[key];
                  const isPublishing = publishingKey === key;

                  return (
                    <tr key={`${row.hiddenId}-${row.publisherUsername}`} className="border-t border-white/10 text-white/70 hover:bg-yellow-500/[0.035]">
                      <td className="px-4 py-3"><div className="font-black text-white">{row.publisherUsername}</div><div className="text-[10px] text-white/35">{row.publisherName}</div></td>
                      <td className="px-4 py-3">{row.hiddenId || "-"}</td>
                      <td className="px-4 py-3">{row.managerName}</td>
                      <td className="px-4 py-3">Kademe {row.previousTier}</td>
                      <td className="px-4 py-3 font-black text-yellow-200">Kademe {row.currentTier}</td>
                      <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${tierChangeClass(row.tierChange)}`}>{row.tierChange}</span></td>
                      <td className="px-4 py-3 font-black text-white">{row.activeDays}</td>
                      <td className="px-4 py-3">{formatNumber(row.liveCount)}</td>
                      <td className="px-4 py-3">{formatDuration(row.liveSeconds)}</td>
                      <td className="px-4 py-3 font-black text-yellow-300">{formatNumber(row.diamonds)}</td>
                      <td className="px-4 py-3">
                        <input
                          value={giftDrafts[key] || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setGiftDrafts((prev) => ({ ...prev, [key]: value }));
                            if (rowMessages[key]) setRowMessages((prev) => ({ ...prev, [key]: null }));
                          }}
                          placeholder="32.000 Elmas / Balina Ödülü"
                          className="w-[260px] rounded-xl border border-yellow-500/25 bg-black/45 px-3 py-2.5 text-xs font-bold text-white outline-none placeholder:text-white/25 focus:border-yellow-400/55"
                        />
                        {message?.text && (
                          <div className={`mt-1 text-[10px] font-black ${message.type === "success" ? "text-emerald-300" : "text-red-300"}`}>
                            {message.type === "success" ? "✓ " : ""}{message.text}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="gold"
                          disabled={isPublishing}
                          onClick={() => publishInlineGift(row)}
                          className="min-w-[92px]"
                        >
                          {isPublishing ? "Yayınlanıyor..." : "Yayınla"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && <tr><td colSpan={12} className="px-5 py-8 text-center text-sm text-white/40">Filtrelere uygun yayıncı bulunamadı.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function normalizeAward(item) {
  return {
    ...item,
    id: item.id,
    periodMonth: item.periodMonth || item.period_month || "",
    publisherUsername: item.publisherUsername || item.publisher_username || "",
    publisherName: item.publisherName || item.publisher_name || "",
    hiddenId: item.hiddenId || item.hidden_id || "",
    managerName: item.managerName || item.manager_name || "",
    previousTier: safeNumber(item.previousTier || item.previous_tier) || 1,
    currentTier: safeNumber(item.currentTier || item.current_tier) || 1,
    tierChange: item.tierChange || item.tier_change || "Kademe Korundu",
    activeDays: safeNumber(item.activeDays || item.active_days),
    liveCount: safeNumber(item.liveCount || item.live_count),
    liveSeconds: safeNumber(item.liveSeconds || item.live_seconds),
    diamonds: safeNumber(item.diamonds),
    giftDetail: item.giftDetail || item.gift_detail || "",
    publishStatus: item.publishStatus || item.publish_status || "Taslak",
    publishedAt: item.publishedAt || item.published_at || "",
    deliveryStatus: item.deliveryStatus || item.delivery_status || "Bekliyor",
    deliveredAt: item.deliveredAt || item.delivered_at || "",
    adminNote: item.adminNote || item.admin_note || "",
    createdBy: item.createdBy || item.created_by || "",
    createdAt: item.createdAt || item.created_at || "",
  };
}

function downloadExcel(fileName, rows) {
  const headers = ["Ay","Kullanıcı Adı","Hidden ID","Yönetici","Eski Kademe","Güncel Kademe","Kademe Değişimi","Aktif Gün","Yayın Adedi","Yayın Süresi","Elmas","Hediye Detayı","Yayınlama Durumu","Yayınlanma Tarihi","Teslim Durumu","Teslim Tarihi","Yönetici Notu","Kaydı Oluşturan","Kayıt Tarihi"];
  const values = rows.map((row) => [row.periodMonth,row.publisherUsername,row.hiddenId,row.managerName,`Kademe ${row.previousTier}`,`Kademe ${row.currentTier}`,row.tierChange,row.activeDays,row.liveCount,formatDuration(row.liveSeconds),row.diamonds,row.giftDetail,row.publishStatus,row.publishedAt,row.deliveryStatus,row.deliveredAt,row.adminNote,row.createdBy,row.createdAt]);
  const clean = (value) => String(value ?? "").replace(/\t/g, " ").replace(/\r?\n/g, " ");
  const content = [headers, ...values].map((row) => row.map(clean).join("\t")).join("\r\n");
  const blob = new Blob(["\ufeff", content], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = fileName; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
}

function downloadTemplate() {
  const content = "Ay\tKullanıcı Adı\tHediye Detayı\r\n2026-07\tornek.yayinci\t32.000 Elmas\r\n2026-07\tornek.yayinci2\tBalina Ödülü";
  const blob = new Blob(["\ufeff", content], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = "Wolf_Media_Hediye_Prim_Sablonu.xls"; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
}

async function enrichImports(parsedRows, publisherRows, managerRows) {
  const publisherMap = {};
  (publisherRows || []).forEach((publisher) => { publisherMap[normalizeUsername(publisher.username)] = publisher; });
  const grouped = {}; const errors = [];
  (parsedRows || []).forEach((row, index) => {
    const periodMonth = String(row.periodMonth || "").trim();
    const username = String(row.publisherUsername || "").trim();
    const giftDetail = String(row.giftDetail || "").trim();
    const publisher = publisherMap[normalizeUsername(username)];
    if (!/^\d{4}-\d{2}$/.test(periodMonth)) return errors.push(`${index + 2}. satır: Ay YYYY-AA formatında olmalı.`);
    if (!publisher) return errors.push(`${index + 2}. satır: ${username || "Kullanıcı"} sistemde bulunamadı.`);
    if (!giftDetail) return errors.push(`${index + 2}. satır: Hediye Detayı boş.`);
    if (!grouped[periodMonth]) grouped[periodMonth] = [];
    grouped[periodMonth].push({ publisher, username, giftDetail });
  });

  const awards = [];
  for (const [periodMonth, items] of Object.entries(grouped)) {
    const publishers = items.map((item) => item.publisher);
    const hiddenIds = publishers.map(getHiddenId).filter(Boolean);
    // eslint-disable-next-line no-await-in-loop
    const [current, previous] = await Promise.all([fetchPerformance(hiddenIds, monthRange(periodMonth)), fetchPerformance(hiddenIds, monthRange(periodMonth, -1))]);
    const snapshots = buildActivityRows(publishers, managerRows, current, previous);
    const map = {}; snapshots.forEach((row) => { map[normalizeUsername(row.publisherUsername)] = row; });
    items.forEach((item) => { const snapshot = map[normalizeUsername(item.username)]; if (snapshot) awards.push({ periodMonth, ...snapshot, giftDetail: item.giftDetail, publishStatus: "Taslak", deliveryStatus: "Bekliyor" }); });
  }
  return { awards, errors };
}

function EditAwardModal({ row, currentAdmin, onClose, onSaved }) {
  const [giftDetail, setGiftDetail] = useState(row?.giftDetail || "");
  const [adminNote, setAdminNote] = useState(row?.adminNote || "");
  const [saving, setSaving] = useState(false);
  if (!row) return null;
  async function save() {
    try {
      setSaving(true);
      await requestJson(API_GIFT_AWARD_SAVE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...row, giftDetail, adminNote, updatedBy: currentAdmin?.username || "admin" }) });
      onSaved?.(); onClose?.();
    } finally { setSaving(false); }
  }
  return <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4"><div className="w-full max-w-xl rounded-[1.6rem] border border-yellow-500/25 bg-[#090909] p-5"><div className="flex items-center justify-between"><h3 className="text-xl font-black text-white">Hediye Kaydını Düzenle</h3><Button onClick={onClose}>Kapat</Button></div><div className="mt-2 text-xs text-white/40">{row.publisherUsername} • {periodLabel(row.periodMonth)}</div><label className="mt-5 block"><span className="mb-2 block text-xs font-black text-white/60">Hediye Detayı</span><input value={giftDetail} onChange={(e) => setGiftDetail(e.target.value)} className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none" /></label><label className="mt-4 block"><span className="mb-2 block text-xs font-black text-white/60">Yönetici Notu</span><textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none" /></label><div className="mt-5 flex justify-end"><Button variant="gold" onClick={save} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</Button></div></div></div>;
}

export function GiftAwardsContent({ publisherRows = [], managerRows = [], currentAdmin = {} }) {
  const [periodMonth, setPeriodMonth] = useState(defaultMonth());
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(false); const [note, setNote] = useState("");
  const [search, setSearch] = useState(""); const [statusFilter, setStatusFilter] = useState(""); const [selectedIds, setSelectedIds] = useState([]); const [editRow, setEditRow] = useState(null);
  const load = useCallback(async () => { try { setLoading(true); const data = await requestJson(`${API_GIFT_AWARD_LIST}?period_month=${encodeURIComponent(periodMonth)}`, { cache: "no-store" }); setRows((data.awards || []).map(normalizeAward)); setSelectedIds([]); } catch (err) { setRows([]); setNote(err.message); } finally { setLoading(false); } }, [periodMonth]);
  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => rows.filter((row) => { const q = search.toLocaleLowerCase("tr-TR"); if (statusFilter && row.publishStatus !== statusFilter) return false; return !q || `${row.publisherUsername} ${row.publisherName} ${row.hiddenId} ${row.giftDetail} ${row.managerName}`.toLocaleLowerCase("tr-TR").includes(q); }), [rows, search, statusFilter]);
  const toggle = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  async function publish(ids, allDrafts = false) { try { setLoading(true); const data = await requestJson(API_GIFT_AWARD_PUBLISH, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids, allDrafts, periodMonth, publishedBy: currentAdmin?.username || "admin" }) }); setNote(data.message || "Yayınlandı."); await load(); } catch (err) { setNote(err.message); setLoading(false); } }
  async function unpublish(id) { try { setLoading(true); await requestJson(API_GIFT_AWARD_UNPUBLISH, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id, updatedBy: currentAdmin?.username || "admin" }) }); await load(); } catch (err) { setNote(err.message); setLoading(false); } }
  async function remove(id) { if (!window.confirm("Bu hediye kaydı silinsin mi?")) return; try { await requestJson(API_GIFT_AWARD_DELETE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) }); await load(); } catch (err) { setNote(err.message); } }
  async function importFile(event) { const file = event.target.files?.[0]; event.target.value = ""; if (!file) return; try { setLoading(true); setNote("Excel okunuyor..."); const fd = new FormData(); fd.append("file", file); const parsed = await requestJson(API_GIFT_AWARD_IMPORT_PREVIEW, { method: "POST", body: fd }); const enriched = await enrichImports(parsed.rows || [], publisherRows, managerRows); if (!enriched.awards.length) { setNote(enriched.errors.join("\n") || "Yüklenebilir kayıt yok."); return; } const saved = await requestJson(API_GIFT_AWARD_BULK_SAVE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ awards: enriched.awards, createdBy: currentAdmin?.username || "admin" }) }); const firstMonth = enriched.awards[0].periodMonth; setNote(`${saved.createdCount || enriched.awards.length} kayıt taslak yüklendi.${enriched.errors.length ? ` ${enriched.errors.length} satır atlandı.` : ""}`); if (firstMonth !== periodMonth) setPeriodMonth(firstMonth); else await load(); } catch (err) { setNote(err.message || "Toplu yükleme başarısız."); } finally { setLoading(false); } }

  return <div><SectionHeader title="Hediye / Prim İşlemleri" desc="Hediye kararları serbest metin olarak kaydedilir. Excel ile toplu yüklenir; kayıtlar önce taslak gelir ve tekli veya toplu yayınlanır." action={<div className="flex flex-wrap gap-2"><Button onClick={downloadTemplate}>Excel Şablonu</Button><Button variant="green" onClick={() => downloadExcel(`hediye-prim-${periodMonth}.xls`, filtered)} disabled={!filtered.length}>Tüm Başlıklarla İndir</Button></div>} />
    <Card className="p-4"><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[180px_1fr_180px_auto]"><Input label="Ay" type="month" value={periodMonth} onChange={(e) => setPeriodMonth(e.target.value)} /><Input label="Ara" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Yayıncı, hediye, Hidden ID" /><Select label="Yayın Durumu" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="">Tümü</option><option>Taslak</option><option>Yayınlandı</option></Select><label className="flex cursor-pointer items-end"><span className="w-full rounded-xl border border-yellow-500/25 bg-yellow-500/10 px-4 py-3 text-center text-xs font-black text-yellow-200">{loading ? "İşleniyor..." : "Excel Yükle"}<input type="file" accept=".xlsx,.csv" onChange={importFile} className="hidden" disabled={loading} /></span></label></div><div className="mt-4 flex flex-wrap gap-2"><Button variant="gold" disabled={!selectedIds.length || loading} onClick={() => publish(selectedIds)}>Seçilenleri Yayınla ({selectedIds.length})</Button><Button variant="gold" disabled={loading || !rows.some((r) => r.publishStatus === "Taslak")} onClick={() => publish([], true)}>Tüm Taslakları Yayınla</Button><Button onClick={load} disabled={loading}>Yenile</Button></div>{note && <div className="mt-4 whitespace-pre-line rounded-xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-3 text-xs font-bold text-yellow-100">{note}</div>}</Card>
    <Card className="mt-5 overflow-hidden"><div className="overflow-x-auto"><table className="min-w-[1450px] w-full border-collapse text-left text-xs"><thead className="bg-white/[0.04] text-[10px] font-black uppercase text-white/40"><tr><th className="px-4 py-3">Seç</th>{["Ay","Yayıncı","Yönetici","Eski","Güncel","Kademe Değişimi","Aktif Gün","Yayın","Süre","Elmas","Hediye Detayı","Yayın Durumu","Teslim","İşlem"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead><tbody>{filtered.map((row) => <tr key={row.id} className="border-t border-white/10 text-white/70"><td className="px-4 py-3"><input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggle(row.id)} className="accent-yellow-400" /></td><td className="px-4 py-3">{periodLabel(row.periodMonth)}</td><td className="px-4 py-3"><b className="text-white">{row.publisherUsername}</b><div className="text-[10px] text-white/35">{row.hiddenId}</div></td><td className="px-4 py-3">{row.managerName}</td><td className="px-4 py-3">K{row.previousTier}</td><td className="px-4 py-3">K{row.currentTier}</td><td className="px-4 py-3"><span className={`rounded-full border px-2 py-1 text-[10px] font-black ${tierChangeClass(row.tierChange)}`}>{row.tierChange}</span></td><td className="px-4 py-3">{row.activeDays}</td><td className="px-4 py-3">{row.liveCount}</td><td className="px-4 py-3">{formatDuration(row.liveSeconds)}</td><td className="px-4 py-3 font-black text-yellow-300">{formatNumber(row.diamonds)}</td><td className="px-4 py-3 font-black text-white">{row.giftDetail}</td><td className="px-4 py-3">{row.publishStatus}</td><td className="px-4 py-3">{row.deliveryStatus}</td><td className="px-4 py-3"><div className="flex flex-wrap gap-1">{row.publishStatus === "Taslak" ? <Button variant="gold" onClick={() => publish([row.id])}>Yayınla</Button> : <Button onClick={() => unpublish(row.id)}>Yayından Kaldır</Button>}<Button onClick={() => setEditRow(row)}>Düzenle</Button><Button variant="red" onClick={() => remove(row.id)}>Sil</Button></div></td></tr>)}{!filtered.length && <tr><td colSpan={15} className="px-5 py-8 text-center text-white/40">Kayıt bulunamadı.</td></tr>}</tbody></table></div></Card>
    <EditAwardModal row={editRow} currentAdmin={currentAdmin} onClose={() => setEditRow(null)} onSaved={load} />
  </div>;
}

function MonthlyTracking({ currentAdmin = {}, reportMode = false }) {
  const [periodMonth, setPeriodMonth] = useState(defaultMonth()); const [rows, setRows] = useState([]); const [search, setSearch] = useState(""); const [deliveryFilter, setDeliveryFilter] = useState(""); const [loading, setLoading] = useState(false); const [note, setNote] = useState("");
  const load = useCallback(async () => { try { setLoading(true); const data = await requestJson(`${API_GIFT_AWARD_LIST}?period_month=${encodeURIComponent(periodMonth)}`, { cache: "no-store" }); setRows((data.awards || []).map(normalizeAward)); setNote(""); } catch (err) { setRows([]); setNote(err.message); } finally { setLoading(false); } }, [periodMonth]);
  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => rows.filter((row) => { const q = search.toLocaleLowerCase("tr-TR"); if (deliveryFilter && row.deliveryStatus !== deliveryFilter) return false; return !q || `${row.publisherUsername} ${row.managerName} ${row.giftDetail}`.toLocaleLowerCase("tr-TR").includes(q); }), [rows, search, deliveryFilter]);
  const publisherCount = new Set(rows.map((r) => normalizeUsername(r.publisherUsername))).size; const publishedCount = rows.filter((r) => r.publishStatus === "Yayınlandı").length; const deliveredCount = rows.filter((r) => r.deliveryStatus === "Teslim Edildi").length; const pendingCount = rows.filter((r) => !["Gönderildi","Teslim Edildi"].includes(r.deliveryStatus)).length;
  async function updateDelivery(row, status) { try { setLoading(true); await requestJson(API_GIFT_AWARD_DELIVERY_UPDATE, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: row.id, deliveryStatus: status, updatedBy: currentAdmin?.username || "admin" }) }); await load(); } catch (err) { setNote(err.message); setLoading(false); } }
  return <div><SectionHeader title={reportMode ? "Hediye / Prim Raporları" : "Aylık Gönderim Takibi"} desc={reportMode ? "Aylık hediye, performans snapshot, yayın ve teslim bilgileri tüm başlıklarla raporlanır." : "Hangi ay kime ne gönderildiği ve teslim durumu tek ekranda takip edilir."} action={<Button variant="green" disabled={!filtered.length} onClick={() => downloadExcel(`hediye-prim-raporu-${periodMonth}.xls`, filtered)}>Excel İndir</Button>} />
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"><Card className="p-4"><small className="text-white/35">YAYINCI</small><div className="mt-2 text-2xl font-black">{publisherCount}</div></Card><Card className="p-4"><small className="text-white/35">YAYINLANAN</small><div className="mt-2 text-2xl font-black text-emerald-200">{publishedCount}</div></Card><Card className="p-4"><small className="text-white/35">BEKLEYEN</small><div className="mt-2 text-2xl font-black text-yellow-200">{pendingCount}</div></Card><Card className="p-4"><small className="text-white/35">TESLİM EDİLEN</small><div className="mt-2 text-2xl font-black text-cyan-200">{deliveredCount}</div></Card></div>
    <Card className="mt-4 p-4"><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"><Input label="Ay" type="month" value={periodMonth} onChange={(e) => setPeriodMonth(e.target.value)} /><Input label="Ara" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Yayıncı, yönetici, hediye" /><Select label="Teslim Durumu" value={deliveryFilter} onChange={(e) => setDeliveryFilter(e.target.value)}><option value="">Tümü</option>{["Bekliyor","Planlandı","Gönderildi","Teslim Edildi","İptal"].map((s) => <option key={s}>{s}</option>)}</Select><div className="flex items-end"><Button className="w-full" onClick={load}>Yenile</Button></div></div>{note && <div className="mt-3 rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-3 text-xs text-yellow-100">{note}</div>}</Card>
    <Card className="mt-5 overflow-hidden"><div className="overflow-x-auto"><table className="min-w-[1450px] w-full text-left text-xs"><thead className="bg-white/[0.04] text-[10px] uppercase text-white/40"><tr>{["Yayıncı","Yönetici","Eski","Güncel","Değişim","Aktif Gün","Yayın","Süre","Elmas","Hediye","Yayın Durumu","Yayın Tarihi","Teslim Durumu","Teslim Tarihi"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead><tbody>{filtered.map((row) => <tr key={row.id} className="border-t border-white/10 text-white/70"><td className="px-4 py-3"><b className="text-white">{row.publisherUsername}</b><div className="text-[10px] text-white/35">{row.hiddenId}</div></td><td className="px-4 py-3">{row.managerName}</td><td className="px-4 py-3">K{row.previousTier}</td><td className="px-4 py-3">K{row.currentTier}</td><td className="px-4 py-3">{row.tierChange}</td><td className="px-4 py-3">{row.activeDays}</td><td className="px-4 py-3">{row.liveCount}</td><td className="px-4 py-3">{formatDuration(row.liveSeconds)}</td><td className="px-4 py-3 text-yellow-300">{formatNumber(row.diamonds)}</td><td className="px-4 py-3 font-black text-white">{row.giftDetail}</td><td className="px-4 py-3">{row.publishStatus}</td><td className="px-4 py-3">{row.publishedAt || "-"}</td><td className="px-4 py-3"><select value={row.deliveryStatus} onChange={(e) => updateDelivery(row, e.target.value)} disabled={loading} className="rounded-lg border border-white/10 bg-[#0b0b0b] px-2 py-2 text-[11px] text-white">{["Bekliyor","Planlandı","Gönderildi","Teslim Edildi","İptal"].map((s) => <option key={s}>{s}</option>)}</select></td><td className="px-4 py-3">{row.deliveredAt || "-"}</td></tr>)}{!filtered.length && <tr><td colSpan={14} className="px-5 py-8 text-center text-white/40">Kayıt bulunamadı.</td></tr>}</tbody></table></div></Card>
  </div>;
}

export function GiftMonthlyTrackingContent(props) { return <MonthlyTracking {...props} />; }
export function GiftReportsContent(props) { return <MonthlyTracking {...props} reportMode />; }
